import { useState, useRef, useCallback, useEffect } from 'react';
import type { Place } from '../types/Place';
import { calculateInterval } from '../utils/animationTiming';
import { useReducedMotion } from './useReducedMotion';

export type AnimationState = 'idle' | 'playing' | 'complete';

interface UseAnimationResult {
  animationState: AnimationState;
  visiblePlaces: Set<string>;
  startAnimation: () => void;
  resetAnimation: () => void;
}

export function useAnimation(places: Place[]): UseAnimationResult {
  const prefersReducedMotion = useReducedMotion();
  const [animationState, setAnimationState] = useState<AnimationState>('idle');
  const [visiblePlaces, setVisiblePlaces] = useState<Set<string>>(new Set());

  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const intervalMs = calculateInterval(places.length);

  const tick = useCallback((timestamp: number) => {
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const visibleCount = Math.min(
      Math.floor(elapsed / intervalMs) + 1,
      places.length
    );

    // Only update state when visible count actually changes (not every frame)
    setVisiblePlaces(prev => {
      if (prev.size === visibleCount) {
        return prev; // Same count, keep same object reference
      }
      return new Set(places.slice(0, visibleCount).map(p => p.id));
    });

    // Check if animation is complete
    if (visibleCount >= places.length) {
      setAnimationState('complete');
      animationRef.current = null;
      startTimeRef.current = null;
    } else {
      animationRef.current = requestAnimationFrame(tick);
    }
  }, [places, intervalMs]);

  const startAnimation = useCallback(() => {
    if (places.length === 0) return;

    // If user prefers reduced motion, skip animation entirely
    if (prefersReducedMotion) {
      const allPlaceIds = new Set(places.map(p => p.id));
      setVisiblePlaces(allPlaceIds);
      setAnimationState('complete');
      return;
    }

    // Reset state
    setVisiblePlaces(new Set());
    startTimeRef.current = null;
    setAnimationState('playing');

    // Start animation loop
    animationRef.current = requestAnimationFrame(tick);
  }, [places, tick, prefersReducedMotion]);

  const resetAnimation = useCallback(() => {
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    startTimeRef.current = null;
    setAnimationState('idle');
    setVisiblePlaces(new Set());
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    animationState,
    visiblePlaces,
    startAnimation,
    resetAnimation,
  };
}
